import { useState, useEffect, useMemo } from 'react';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '../store/authStore';
import { reportsApi } from '../services/api';
import {
  BarChart3,
  LayoutDashboard,
  FileText,
  Users,
  Globe,
  MapPin,
  TrendingUp,
  Settings,
  LogOut,
  Menu,
  X,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Shield,
  Database,
  Key,
  Search,
  Upload,
  Hash,
  Calculator,
  Bell,
  PanelLeftClose,
  PanelLeft,
  Building2,
  UserCircle,
  AlertTriangle,
  UserX,
  Trash2,
  Bookmark,
} from 'lucide-react';

// Static navigation items (Quick Reports will be built dynamically)
const baseNavigation = [
  { name: 'Dashboard', href: '/', icon: LayoutDashboard },
  {
    name: 'Reports',
    icon: FileText,
    children: [
      { name: 'Analytics', href: '/reports/analytics', icon: BarChart3 },
      { name: 'Affiliate', href: '/reports/affiliate', icon: Users },
      { name: 'Averages', href: '/reports/averages', icon: Calculator },
    ],
  },
];

// Default quick reports that are always shown
const defaultQuickReports = [
  { name: 'Channel Analytics', href: '/reports/analytics?groupBy=channel', icon: BarChart3 },
  { name: 'Country Analytics', href: '/reports/analytics?groupBy=country', icon: Globe },
  { name: 'Device Analytics', href: '/reports/analytics?groupBy=device_type', icon: Shield },
  { name: 'Keyword Analytics', href: '/reports/analytics?groupBy=keyword', icon: Search },
  { name: 'Subchannel Analytics', href: '/reports/analytics?groupBy=subchannel', icon: BarChart3 },
  { name: 'Landing Page', href: '/reports/analytics?groupBy=landing_page', icon: FileText },
];

const restOfNavigation = [
  {
    name: 'Tools',
    icon: Settings,
    children: [
      { name: 'Decode', href: '/reports/decode', icon: Key },
      { name: 'Search Engine Marketing', href: '/reports/sem', icon: Search },
      { name: 'Import Sales', href: '/reports/import-sales', icon: Upload },
      { name: 'Clear IP', href: '/reports/clear-ip', icon: UserX },
      { name: 'Hash Report', href: '/reports/hash', icon: Hash },
      { name: 'UTM Setup', href: '/reports/utm-setup', icon: Settings },
    ],
  },
  { name: 'UTM Setup', href: '/reports/utm-setup', icon: Settings },
  { name: 'Clients', href: '/clients', icon: Users },
  { name: 'Advertisers', href: '/advertisers', icon: Building2 },
  { name: 'IP Actions', href: '/ip-actions', icon: Shield },
  { name: 'Settings', href: '/settings', icon: Settings },
  { name: 'Accounts', href: '/accounts', icon: UserCircle },
  { name: 'Submit Issue', href: '/submit-issue', icon: AlertTriangle },
];

function NavItem({ item, collapsed, mobile = false, onNavigate, onDeleteChild }) {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const baseClass = mobile
    ? 'flex items-center px-4 py-3 text-sm font-medium rounded-xl transition-all duration-200'
    : `flex items-center ${collapsed ? 'justify-center px-3' : 'px-3'} py-2.5 text-sm font-medium rounded-xl transition-all duration-200`;

  if (item.children) {
    return (
      <div className="relative group">
        <button
          onClick={() => !collapsed && setOpen(!open)}
          className={`${baseClass} w-full text-secondary-600 hover:bg-primary-50 hover:text-primary-600`}
          title={collapsed ? item.name : undefined}
        >
          <item.icon className={`w-5 h-5 flex-shrink-0 ${collapsed ? '' : 'mr-3'}`} />
          {!collapsed && (
            <>
              <span className="flex-1 text-left">{item.name}</span>
              <ChevronDown
                className={`w-4 h-4 ml-auto transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
              />
            </>
          )}
        </button>

        {/* Expanded dropdown for non-collapsed state */}
        {!collapsed && (
          <div className={`overflow-hidden transition-all duration-200 ${open ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'}`}>
            <div className="mt-1 ml-4 pl-4 border-l-2 border-secondary-100 space-y-1">
              {item.children.map((child) => {
                // Handle divider items
                if (child.isDivider) {
                  return (
                    <div key={child.name} className="px-3 py-1 text-xs font-medium text-secondary-400">
                      {child.name}
                    </div>
                  );
                }

                // For URLs with query params, compare full URL; otherwise use React Router's isActive
                const hasQueryParams = child.href.includes('?');
                const isActiveCustom = hasQueryParams
                  ? location.pathname + location.search === child.href
                  : null;

                return (
                  <div key={child.href || child.id} className="group/item flex items-center">
                    <NavLink
                      to={child.href}
                      onClick={onNavigate}
                      className={({ isActive }) => {
                        const active = hasQueryParams ? isActiveCustom : isActive;
                        return `${baseClass} flex-1 ${
                          active
                            ? 'bg-primary-500 text-white shadow-soft'
                            : 'text-secondary-600 hover:bg-primary-50 hover:text-primary-600'
                        }`;
                      }}
                    >
                      <child.icon className="w-4 h-4 mr-3 flex-shrink-0" />
                      <span className="truncate">{child.name}</span>
                    </NavLink>
                    {child.isDeletable && onDeleteChild && (
                      <button
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          onDeleteChild(child.id);
                        }}
                        className="opacity-0 group-hover/item:opacity-100 p-1 ml-1 text-secondary-400 hover:text-red-500 hover:bg-red-50 rounded transition-all"
                        title="Delete quick report"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Flyout menu for collapsed state */}
        {collapsed && (
          <div className="absolute left-full top-0 ml-2 w-56 bg-white rounded-xl shadow-elevated border border-secondary-100 py-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
            <div className="px-3 py-2 text-xs font-semibold text-secondary-400 uppercase tracking-wider">
              {item.name}
            </div>
            {item.children.map((child) => {
              // Handle divider items
              if (child.isDivider) {
                return (
                  <div key={child.name} className="px-3 py-1 text-xs font-medium text-secondary-400 border-t border-secondary-100 mt-1 pt-2">
                    {child.name}
                  </div>
                );
              }

              const hasQueryParams = child.href.includes('?');
              const isActiveCustom = hasQueryParams
                ? location.pathname + location.search === child.href
                : null;

              return (
                <div key={child.href || child.id} className="group/item flex items-center mx-2">
                  <NavLink
                    to={child.href}
                    onClick={onNavigate}
                    className={({ isActive }) => {
                      const active = hasQueryParams ? isActiveCustom : isActive;
                      return `flex items-center flex-1 px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                        active
                          ? 'bg-primary-500 text-white'
                          : 'text-secondary-600 hover:bg-primary-50 hover:text-primary-600'
                      }`;
                    }}
                  >
                    <child.icon className="w-4 h-4 mr-3 flex-shrink-0" />
                    <span className="truncate">{child.name}</span>
                  </NavLink>
                  {child.isDeletable && onDeleteChild && (
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        onDeleteChild(child.id);
                      }}
                      className="opacity-0 group-hover/item:opacity-100 p-1 text-secondary-400 hover:text-red-500 hover:bg-red-50 rounded transition-all"
                      title="Delete quick report"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  return (
    <NavLink
      to={item.href}
      onClick={onNavigate}
      title={collapsed ? item.name : undefined}
      className={({ isActive }) =>
        `${baseClass} ${
          isActive
            ? 'bg-primary-500 text-white shadow-soft'
            : 'text-secondary-600 hover:bg-primary-50 hover:text-primary-600'
        }`
      }
    >
      <item.icon className={`w-5 h-5 flex-shrink-0 ${collapsed ? '' : 'mr-3'}`} />
      {!collapsed && item.name}
    </NavLink>
  );
}

export default function MainLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(() => {
    const saved = localStorage.getItem('sidebar-collapsed');
    return saved ? JSON.parse(saved) : false;
  });
  const { user, logout } = useAuthStore();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Fetch user's quick reports
  const { data: quickReportsData } = useQuery({
    queryKey: ['quick-reports'],
    queryFn: () => reportsApi.getQuickReports(),
    staleTime: 5 * 60 * 1000,
    retry: false, // Don't retry if the endpoint fails (e.g., migration not run)
  });

  // Delete quick report mutation
  const deleteQuickReportMutation = useMutation({
    mutationFn: (id) => reportsApi.deleteReport(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['quick-reports'] });
    },
  });

  // Build dynamic navigation with user's quick reports
  const navigation = useMemo(() => {
    const userQuickReports = (quickReportsData?.data?.data || []).map((report) => ({
      id: report.id,
      name: report.name,
      href: `/reports/analytics?savedReport=${report.id}`,
      icon: Bookmark,
      isDeletable: true,
    }));

    const quickReportsSection = {
      name: 'Quick Reports',
      icon: BarChart3,
      children: [
        ...defaultQuickReports,
        ...(userQuickReports.length > 0 ? [
          { name: '— Saved Reports —', href: '#divider', icon: Bookmark, isDivider: true },
          ...userQuickReports,
        ] : []),
      ],
    };

    return [
      ...baseNavigation,
      quickReportsSection,
      ...restOfNavigation,
    ];
  }, [quickReportsData]);

  const handleDeleteQuickReport = (id) => {
    if (window.confirm('Are you sure you want to delete this quick report?')) {
      deleteQuickReportMutation.mutate(id);
    }
  };

  useEffect(() => {
    localStorage.setItem('sidebar-collapsed', JSON.stringify(collapsed));
  }, [collapsed]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const closeSidebar = () => {
    setSidebarOpen(false);
  };

  const toggleCollapse = () => {
    setCollapsed(!collapsed);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-secondary-50 to-secondary-100">
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-secondary-900/60 backdrop-blur-sm lg:hidden"
          onClick={closeSidebar}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed inset-y-0 left-0 z-50 bg-white shadow-elevated transform transition-all duration-300 ease-in-out lg:translate-x-0 flex flex-col ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        } ${collapsed ? 'w-20' : 'w-72'}`}
      >
        {/* Logo */}
        <div className={`flex items-center justify-between h-20 px-4 border-b border-secondary-100 flex-shrink-0 ${collapsed ? 'justify-center' : ''}`}>
          <div className="flex items-center">
            {collapsed ? (
              <div className="w-10 h-10 bg-gradient-to-br from-primary-400 to-primary-600 rounded-xl flex items-center justify-center">
                <span className="text-white font-bold text-lg">L</span>
              </div>
            ) : (
              <img
                src="/logo-dark.png"
                alt="LazySauce"
                className="h-10 w-auto"
              />
            )}
          </div>
          <button
            onClick={closeSidebar}
            className="lg:hidden p-2 rounded-xl hover:bg-secondary-100 transition-colors"
          >
            <X className="w-5 h-5 text-secondary-500" />
          </button>
        </div>

        {/* Collapse toggle button - desktop only */}
        <button
          onClick={toggleCollapse}
          className="hidden lg:flex absolute -right-3 top-24 w-6 h-6 bg-white border border-secondary-200 rounded-full items-center justify-center shadow-sm hover:shadow-md hover:bg-secondary-50 transition-all z-10"
          title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {collapsed ? (
            <ChevronRight className="w-4 h-4 text-secondary-500" />
          ) : (
            <ChevronLeft className="w-4 h-4 text-secondary-500" />
          )}
        </button>

        {/* Navigation */}
        <nav className={`flex-1 overflow-y-auto p-3 space-y-1 ${collapsed ? 'px-2' : 'p-4'}`}>
          {navigation.map((item) => (
            <NavItem
              key={item.name}
              item={item}
              collapsed={collapsed}
              onNavigate={closeSidebar}
              onDeleteChild={item.name === 'Quick Reports' ? handleDeleteQuickReport : undefined}
            />
          ))}
        </nav>

        {/* User section */}
        <div className={`border-t border-secondary-100 flex-shrink-0 bg-gradient-to-t from-secondary-50 to-white ${collapsed ? 'p-2' : 'p-4'}`}>
          {collapsed ? (
            <div className="flex flex-col items-center gap-2">
              <div className="w-10 h-10 bg-gradient-to-br from-primary-400 to-primary-600 rounded-xl flex items-center justify-center shadow-soft" title={user?.username}>
                <span className="text-white font-semibold">
                  {user?.username?.charAt(0).toUpperCase() || 'U'}
                </span>
              </div>
              <button
                onClick={handleLogout}
                className="p-2 text-secondary-500 hover:text-red-600 hover:bg-red-50 rounded-xl transition-colors"
                title="Sign Out"
              >
                <LogOut className="w-5 h-5" />
              </button>
            </div>
          ) : (
            <>
              <div className="flex items-center p-3 rounded-xl bg-white shadow-card mb-3">
                <div className="w-11 h-11 bg-gradient-to-br from-primary-400 to-primary-600 rounded-xl flex items-center justify-center flex-shrink-0 shadow-soft">
                  <span className="text-white font-semibold text-lg">
                    {user?.username?.charAt(0).toUpperCase() || 'U'}
                  </span>
                </div>
                <div className="ml-3 min-w-0 flex-1">
                  <p className="text-sm font-semibold text-secondary-900 truncate">
                    {user?.username || 'User'}
                  </p>
                  <p className="text-xs text-secondary-500 capitalize truncate">
                    {user?.role || 'User'}
                  </p>
                </div>
              </div>
              <button
                onClick={handleLogout}
                className="flex items-center w-full px-4 py-2.5 text-sm font-medium text-secondary-600 rounded-xl hover:bg-red-50 hover:text-red-600 transition-all duration-200"
              >
                <LogOut className="w-5 h-5 mr-3" />
                Sign Out
              </button>
            </>
          )}
        </div>
      </aside>

      {/* Main content */}
      <div className={`transition-all duration-300 ${collapsed ? 'lg:pl-20' : 'lg:pl-72'}`}>
        {/* Top bar */}
        <header className="sticky top-0 z-30 bg-white/80 backdrop-blur-md border-b border-secondary-100">
          <div className="flex items-center justify-between h-16 px-4 lg:px-6">
            <div className="flex items-center gap-3">
              <button
                onClick={() => setSidebarOpen(true)}
                className="lg:hidden p-2 rounded-xl hover:bg-secondary-100 transition-colors"
              >
                <Menu className="w-5 h-5 text-secondary-600" />
              </button>

              {/* Collapse toggle for mobile - shows in header */}
              <button
                onClick={toggleCollapse}
                className="hidden lg:flex p-2 rounded-xl hover:bg-secondary-100 transition-colors"
                title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
              >
                {collapsed ? (
                  <PanelLeft className="w-5 h-5 text-secondary-500" />
                ) : (
                  <PanelLeftClose className="w-5 h-5 text-secondary-500" />
                )}
              </button>
            </div>

            <div className="flex items-center gap-3">
              <button className="relative p-2 rounded-xl hover:bg-secondary-100 transition-colors">
                <Bell className="w-5 h-5 text-secondary-500" />
                <span className="absolute top-1.5 right-1.5 w-2 h-2 bg-primary-500 rounded-full"></span>
              </button>
              <div className="hidden sm:flex items-center gap-3 pl-3 border-l border-secondary-200">
                <div className="text-right">
                  <p className="text-sm font-semibold text-secondary-900">
                    {user?.username}
                  </p>
                  <p className="text-xs text-secondary-500 capitalize">
                    {user?.role || 'User'}
                  </p>
                </div>
                <div className="w-10 h-10 bg-gradient-to-br from-primary-400 to-primary-600 rounded-xl flex items-center justify-center shadow-soft">
                  <span className="text-white font-semibold">
                    {user?.username?.charAt(0).toUpperCase() || 'U'}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="p-4 lg:p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
